import { Fragment, type ReactNode, useState } from "react";
import { Button } from "@design-system/Button/Button";
import {
    Modal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import styles from "./ScheduleManualModal.module.scss";

export interface ScheduleExpandableModalContent<TItem> {
    item: TItem;
    isExpanded: boolean;
    onExpandedChange: (isExpanded: boolean) => void;
}

interface ScheduleExpandableModalProps<TItem> {
    /** What the modal shows; null closes it. */
    item: TItem | null;
    onHide: () => void;
    children: (content: ScheduleExpandableModalContent<TItem>) => ReactNode;
}

/**
 * The large schedule modal that flips to full screen. Keeps the last item
 * mounted while the modal animates closed; every open remounts the content
 * and collapses it, even when the close animation was cut short.
 */
export const ScheduleExpandableModal = <TItem,>({
    item,
    onHide,
    children,
}: ScheduleExpandableModalProps<TItem>) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [shownItem, setShownItem] = useState(item);
    const [previousItem, setPreviousItem] = useState(item);
    const [openCount, setOpenCount] = useState(0);
    if (item !== previousItem) {
        setPreviousItem(item);
        if (item !== null) {
            setShownItem(item);
            setIsExpanded(false);
            setOpenCount((count) => count + 1);
        }
    }
    return (
        <Modal
            show={item !== null}
            onHide={onHide}
            onExited={() => {
                setShownItem(null);
                setIsExpanded(false);
            }}
            size="lg"
            fullscreen={isExpanded}
        >
            {shownItem !== null && (
                <Fragment key={openCount}>
                    {children({
                        item: shownItem,
                        isExpanded,
                        onExpandedChange: setIsExpanded,
                    })}
                </Fragment>
            )}
        </Modal>
    );
};

const LOAD_ERROR_MESSAGE = "Failed to load schedule. Close and try again.";

export const ScheduleModalLoadError = ({
    title,
    onHide,
}: {
    title: string;
    onHide: () => void;
}) => (
    <>
        <StandardModalHeader className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
        </StandardModalHeader>
        <StandardModalBody>
            <p className={styles.previewError}>{LOAD_ERROR_MESSAGE}</p>
        </StandardModalBody>
        <StandardModalFooter>
            <Button type="button" variant="secondary" onClick={onHide}>
                Close
            </Button>
        </StandardModalFooter>
    </>
);
